import { errorTracker } from "@dashboard/services/errorTracking";
import { useCallback, useEffect, useRef, useState } from "react";

import { useSidebarWebhookAlertMetadata } from "./useSidebarWebhookAlertMetadata";

const shouldShowDotCheck = (
  lastClickDate: string | undefined,
  lastFailedAttemptDate: string | undefined,
): boolean => {
  if (!lastFailedAttemptDate) {
    return false;
  }

  return !lastClickDate || lastFailedAttemptDate > lastClickDate;
};

interface SidebarDotState {
  handleAppsListItemClick: (clickDate: string) => void;
  handleFailedAttempt: (failedAttemptDate: string) => void;
  hasProblems: boolean;
}

export const useSidebarDotState = (): SidebarDotState => {
  const { persist, sidebarDotRemoteState } = useSidebarWebhookAlertMetadata();

  const [hasProblems, setHasProblems] = useState(false);
  const lastClickDateRef = useRef<string | null>(null);
  const lastFailedAttemptDateRef = useRef<string | null>(null);

  // Initialize state from the remote state
  useEffect(() => {
    if (sidebarDotRemoteState) {
      lastClickDateRef.current = sidebarDotRemoteState.lastClickDate;
      lastFailedAttemptDateRef.current = sidebarDotRemoteState.lastFailedAttemptDate;

      const shouldShowDot = shouldShowDotCheck(
        lastClickDateRef.current,
        lastFailedAttemptDateRef.current,
      );

      setHasProblems(shouldShowDot);
    }
  }, [sidebarDotRemoteState]);

  const handleClick = useCallback(
    async (clickDate: string) => {
      try {
        await persist({
          lastClickDate: clickDate,
          lastFailedAttemptDate: lastFailedAttemptDateRef.current ?? "",
        });
        lastClickDateRef.current = clickDate;
      } catch (error) {
        errorTracker.captureException(error as Error);
      }
    },
    [persist],
  );

  const handleFailedAttempt = useCallback(
    async (failedAttemptDate: string) => {
      try {
        await persist({
          lastClickDate: lastClickDateRef.current ?? "",
          lastFailedAttemptDate: failedAttemptDate,
        });
        lastFailedAttemptDateRef.current = failedAttemptDate;

        // Show dot if no clicks or failed attempt is newer
        if (!lastClickDateRef.current || failedAttemptDate > lastClickDateRef.current) {
          setHasProblems(true);
        }
      } catch (error) {
        errorTracker.captureException(error as Error);
      }
    },
    [persist],
  );

  return {
    handleAppsListItemClick: handleClick,
    handleFailedAttempt,
    hasProblems,
  };
};
